// IndexedDB wrapper — offline cache for clients + queue for pending operations.

import { openDB } from "idb";

const DB_NAME = "vidoc-offline";
const DB_VERSION = 1;

const STORE_CLIENTS = "clients_cache";
const STORE_PENDING = "pending_ops";
const STORE_META = "meta";

let dbPromise = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_CLIENTS)) {
          db.createObjectStore(STORE_CLIENTS, { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains(STORE_PENDING)) {
          db.createObjectStore(STORE_PENDING, {
            keyPath: "id",
            autoIncrement: true,
          });
        }
        if (!db.objectStoreNames.contains(STORE_META)) {
          db.createObjectStore(STORE_META, { keyPath: "key" });
        }
      },
    });
  }
  return dbPromise;
}

/* ─────────────────── Clients cache ─────────────────── */

export async function cacheClients(clients) {
  const db = await getDb();
  const tx = db.transaction(STORE_CLIENTS, "readwrite");
  // Reset cache to avoid stale archived entries
  await tx.store.clear();
  for (const c of clients) {
    if (c?.id) await tx.store.put(c);
  }
  await tx.done;
  await setMeta("clients_cached_at", new Date().toISOString());
}

export async function getCachedClients() {
  const db = await getDb();
  return db.getAll(STORE_CLIENTS);
}

export async function getCachedClient(id) {
  const db = await getDb();
  return db.get(STORE_CLIENTS, id);
}

/* ─────────────────── Pending operations queue ─────────────────── */

export async function enqueueOperation(payload) {
  const db = await getDb();
  const id = await db.add(STORE_PENDING, {
    ...payload,
    queued_at: new Date().toISOString(),
  });
  return id;
}

export async function getPendingOperations() {
  const db = await getDb();
  return db.getAll(STORE_PENDING);
}

export async function removePendingOperation(id) {
  const db = await getDb();
  return db.delete(STORE_PENDING, id);
}

export async function countPendingOperations() {
  const db = await getDb();
  return db.count(STORE_PENDING);
}

/* ─────────────────── Meta ─────────────────── */

export async function setMeta(key, value) {
  const db = await getDb();
  await db.put(STORE_META, { key, value });
}

export async function getMeta(key) {
  const db = await getDb();
  const row = await db.get(STORE_META, key);
  return row?.value ?? null;
}
